import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/knowledge-api';

import { useDocsPageContext } from '../../../hooks/useDocsPageContext';
import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import { CONTENT_TYPE } from '../../../constants/docs';

import { withFoldersAndDocsActions } from '../../../hocs/withFoldersAndDocsActions';

import { handleExpandSidePanelFolder } from '../../../helpers/docsSidePanelUtils';

import Alert from '../../../design-system/Alert/Alert';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import CheckLineIcon from '../../../design-system/Icons/CheckLineIcon';
import ArrowRightSLineIcon from '../../../design-system/Icons/ArrowRightSLineIcon';
import FolderBreadcrumbItem from '../FolderBreadcrumbItem/FolderBreadcrumbItem';
import DocCardMoreOptionsPopup from '../DocCardMoreOptionsPopup/DocCardMoreOptionsPopup';

const FolderBreadcrumbs = ({ folderId, setActionModal }) => {
    const navigate = useNavigate();

    const { location, setLocation, setExpandedFolders } = useDocsPageContext();

    const controller = useRef(new AbortController());

    const { isDesktop } = useResponsiveBreakpoints();
    const [isFullLocationDisplayed, setIsFullLocationDisplayed] = useState(isDesktop);

    const [moreOptionsPopup, setMoreOptionsPopup] = useState(null);
    const [successAlert, setSuccessAlert] = useState(null);
    const [errorAlert, setErrorAlert] = useState(null);

    const mainContainerRef = useRef(null);
    const scrollContainerRef = useRef(null);

    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    const handleNavigateToIndex = () => {
        navigate('/docs');
        setLocation([]);
    };

    const fetchFolderDetails = async () => {
        try {
            controller.current?.abort();

            const newController = new AbortController();
            controller.current = newController;

            const { data } = await client.get(API.ROUTES.knowledge.folder + folderId + '/', {
                signal: newController.signal,
            });
            const { location: validLocation } = data;
            const isLocationCorrect = JSON.stringify(location) === JSON.stringify(validLocation);
            if (!isLocationCorrect) {
                handleExpandSidePanelFolder({
                    location: validLocation?.map((loc) => loc.id),
                    setExpandedFolders,
                });
                setLocation(validLocation);
            }
        } catch (e) {
            handlePageDataLoadError({
                e,
                redirectPath: '/docs',
                generalErrorHandler: handleNavigateToIndex,
            });
        }
    };

    useEffect(() => {
        if (folderId) {
            fetchFolderDetails();
        }

        if (!folderId) {
            setLocation([]);
            controller.current?.abort();
        }

        const isWrongPopupOpened = moreOptionsPopup && moreOptionsPopup.data?.id !== folderId;
        if (isWrongPopupOpened) {
            setMoreOptionsPopup(null);
        }
    }, [folderId]);

    useEffect(() => {
        if (!location && folderId) {
            fetchFolderDetails();
        }

        if (!location && !folderId) {
            setLocation([]);
        }
    }, [location]);

    useEffect(() => {
        if (!isDesktop) {
            setIsFullLocationDisplayed(false);
        }
    }, [location]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
    }, [isFullLocationDisplayed, location]);

    if (!folderId) {
        return (
            <div
                onClick={() => setLocation([])}
                className="flex items-center gap-1 font-body-medium text-body-regular-s text-neutral-500"
            >
                <p>Docs</p>
                <ArrowRightSLineIcon width={10} height={10} color="#5E6470" />
            </div>
        );
    }

    if (!location) {
        return <></>;
    }

    const handleItemClick = (id, index) => {
        const slicedLocation = location.slice(0, index + 1);
        handleExpandSidePanelFolder({
            location: slicedLocation?.map((loc) => loc.id),
            setExpandedFolders,
        });

        if (id === folderId) {
            return;
        }

        setLocation(slicedLocation || []);
        navigate(`/docs/folder/${id}`);
    };

    const currentFolderParentLocation = location.slice(0, location.length - 1);

    const openDuplicatedFolder = ({ location }) => {
        setLocation(location);

        handleExpandSidePanelFolder({
            location: location.map((loc) => loc.id),
            setExpandedFolders,
        });
    };

    const openMoreOptionsPopup = ({ data, buttonRect }) => {
        const breadcrumbsRect = mainContainerRef.current?.getBoundingClientRect();

        if (!breadcrumbsRect || !buttonRect) {
            setMoreOptionsPopup({ data, position: 'top-[28px] left-0 !z-[41]' });
        }

        const position = { top: '28px', zIndex: 41 };

        const { left: breadcrumbsLeft } = breadcrumbsRect;
        const { left: buttonLeft } = buttonRect;

        const leftDistance = buttonLeft - breadcrumbsLeft;

        if (leftDistance < 250) {
            position.left = `${leftDistance - 20}px`;
        } else {
            position.left = `${leftDistance - 250}px`;
        }

        setMoreOptionsPopup({ data, position });
    };

    return (
        <div ref={mainContainerRef} className="relative">
            <div
                ref={scrollContainerRef}
                className="flex items-center gap-1 max-w-full overflow-x-auto pb-1"
            >
                <button
                    onClick={handleNavigateToIndex}
                    className="flex items-center gap-1 font-body-medium text-body-regular-xs text-neutral-300"
                >
                    <p>Docs</p>
                    <ArrowRightSLineIcon width={10} height={10} color="#5E6470" />
                </button>

                {location?.map((item, index) => {
                    const isHidden =
                        !isDesktop && index < location.length - 1 && !isFullLocationDisplayed;

                    return (
                        <FolderBreadcrumbItem
                            key={item.id}
                            item={item}
                            index={index}
                            folderId={folderId}
                            isHidden={isHidden}
                            setIsFullLocationDisplayed={setIsFullLocationDisplayed}
                            handleItemClick={handleItemClick}
                            moreOptionsPopup={moreOptionsPopup}
                            openMoreOptionsPopup={openMoreOptionsPopup}
                        />
                    );
                })}
            </div>

            {moreOptionsPopup && (
                <DocCardMoreOptionsPopup
                    contentType={CONTENT_TYPE.folder}
                    data={{ ...moreOptionsPopup.data, location }}
                    parentLocation={currentFolderParentLocation}
                    setActionModal={setActionModal}
                    setSuccessAlert={setSuccessAlert}
                    setErrorAlert={setErrorAlert}
                    onNavigateToFolderView={openDuplicatedFolder}
                    width={270}
                    position={moreOptionsPopup.position}
                    onClose={() => setMoreOptionsPopup(null)}
                />
            )}

            {successAlert && (
                <Alert
                    status="positive"
                    message={successAlert.message}
                    icon={CheckLineIcon}
                    description={successAlert.description}
                    position="top-center"
                    autoCloseInMS={5000}
                    handleClose={() => setSuccessAlert(null)}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default withFoldersAndDocsActions(FolderBreadcrumbs);
